import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import useFetch from "../../hooks/useFetch";

const PDModerator = () => {
  const authState   = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const token       = authState?.token;

  const [isAllowed, setIsAllowed] = useState(false);
  useEffect(() => {
    setIsAllowed(authState?.user?.think5whyscode === "TU243BM5850150");
  }, [authState]);

  const [records,        setRecords]        = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loadingRowId,   setLoadingRowId]   = useState(null); // ✅ per-row loading
  const [loading,        setLoading]        = useState(false);
  const [filters,        setFilters]        = useState({ userCode: "", startDate: "", endDate: "" });
  const [modForm,        setModForm]        = useState({ isLocked: false, status: "submitted", moderatorRemarks: "" });
  const [msg,            setMsg]            = useState({ text: "", type: "" });

  const showMsg  = (text, type = "info") => setMsg({ text, type });
  const clearMsg = () => setMsg({ text: "", type: "" });

  // ── Search ──
  const handleSearch = async () => {
    const { userCode, startDate, endDate } = filters;
    if (!userCode && !startDate && !endDate) {
      showMsg("Please enter a Think5Whys Code or a date range to search.", "warning");
      return;
    }
    try {
      setLoading(true);
      clearMsg();
      const res = await fetchData({
        url: "/pd/orientation/list",
        method: "get",
        headers: { Authorization: token },
      });
      if (res?.success) {
        let list = res.data || [];
        if (userCode)   list = list.filter(r => r.userCode?.toLowerCase().includes(userCode.toLowerCase()));
        // ✅ Fix: endDate now includes the full day
        if (startDate)  list = list.filter(r => new Date(r.createdAt) >= new Date(startDate));
        if (endDate)    list = list.filter(r => new Date(r.createdAt) <= new Date(endDate + "T23:59:59"));
        setRecords(list);
        if (list.length === 0) showMsg("No records found for the given filter.", "info");
      } else {
        showMsg("Failed to fetch records.", "error");
      }
    } catch (err) {
      console.error("handleSearch failed:", err);
      showMsg("Error fetching records.", "error");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ userCode: "", startDate: "", endDate: "" });
    setRecords([]);
    clearMsg();
  };

  // ── Open detail ──
  const openDetails = async (rec) => {
    // ✅ Fix: fallback userId → _id, and track per-row loading
    const id = rec.userId || rec._id;
    try {
      setLoadingRowId(rec._id);
      clearMsg();
      const res = await fetchData({
        url: "/pd/orientation/get",
        method: "post",
        data: { userId: id },
        headers: { Authorization: token },
      });
      if (res?.success) {
        setSelectedRecord(res.data);
        setModForm({
          isLocked:         !!res.data.isLocked,
          status:           res.data.status || "submitted",
          moderatorRemarks: res.data.moderatorRemarks || "",
        });
      } else {
        showMsg(res?.message || "Failed to load record details.", "error");
      }
    } catch (err) {
      console.error("openDetails failed:", err);
      showMsg(err?.response?.data?.message || "Error loading record.", "error");
    } finally {
      setLoadingRowId(null);
    }
  };

  const closeDetails = () => {
    setSelectedRecord(null);
    setModForm({ isLocked: false, status: "submitted", moderatorRemarks: "" });
  };

  // ── Moderate ──
  const handleModerate = async () => {
    // ✅ Fix: confirm before saving
    if (!window.confirm(`Are you sure you want to update this record?\nStatus: ${modForm.status} | Locked: ${modForm.isLocked}`))
      return;
    try {
      setLoading(true);
      clearMsg();
      const res = await fetchData({
        url: `/pd/orientation/${selectedRecord._id}/moderate`,
        method: "patch",
        data: { ...modForm, lockedBy: authState?.user?.name || "Moderator" },
        headers: { Authorization: token },
      });
      if (res?.success) {
        showMsg("Record updated successfully.", "success");
        closeDetails();
        handleSearch();
      } else {
        showMsg(res?.message || "Update failed.", "error");
      }
    } catch (err) {
      console.error("handleModerate failed:", err);
      showMsg("Error updating record.", "error");
    } finally {
      setLoading(false);
    }
  };

  const iClass = "bg-gray-900 border border-gray-700 text-xs lg:text-sm rounded-sm px-2 py-1 focus:outline-none focus:border-indigo-500 placeholder-gray-500 text-white w-full";

  return (
    <div className="space-y-2 bg-gray-900 p-4 rounded-xl text-white border-t border-cyan-200 mt-2">
      <h1 className="text-sm lg:text-lg font-bold text-cyan-200">
        Orientation Moderator : {authState?.user?.name}
      </h1>

      {!isAllowed && (
        <div className="min-h-[300px] flex items-center justify-center p-6">
          <div className="max-w-md w-full rounded-2xl border border-red-700 bg-gradient-to-br from-red-950 via-gray-900 to-red-950 p-6 text-center shadow-xl">
            <div className="text-xl lg:text-2xl mb-2">🔒</div>
            <h2 className="text-xl font-bold text-red-400 mb-2">Access Restricted</h2>
            <p className="text-gray-100 text-sm leading-relaxed mb-5">
              You are not authorized to access this section. Please contact the platform administrator if you believe this is an error.
            </p>
            <button onClick={() => window.history.back()}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-semibold px-4 py-1 rounded-xl transition">
              Close & Exit
            </button>
          </div>
        </div>
      )}

      {isAllowed && (
        <>
          {msg.text && (
            <div className={`flex items-center justify-between flex-nowrap gap-2 text-xs px-2 py-1 rounded-xl ${
              msg.type === "success" ? "bg-green-900/40 text-green-300 border border-green-700"
              : msg.type === "error"   ? "bg-red-900/40 text-red-300 border border-red-700"
              : msg.type === "warning" ? "bg-yellow-900/40 text-yellow-300 border border-yellow-700"
              : "bg-blue-900/40 text-blue-300 border border-blue-700"
            }`}>
              <span className="flex-1 min-w-0 text-xs lg:text-sm">{msg.text}</span>
              <button onClick={clearMsg} className="shrink-0 font-bold text-xs lg:text-sm px-2 hover:opacity-70">×</button>
            </div>
          )}

          <div className="bg-gray-800 p-2 rounded-xl space-y-3 min-h-[70vh]">
            {/* Filters */}
            <div className="bg-[#111827] border border-gray-700 rounded-xl p-3 mb-3">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-cyan-200">🔍 Search Filters</h3>
                <p className="text-[11px] text-gray-400">Provide at least one filter to search records</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-300 mb-1">Think5Whys Code</label>
                  <input type="text" placeholder="e.g. TU243BM5850150"
                    value={filters.userCode}
                    onChange={e => setFilters(f => ({ ...f, userCode: e.target.value }))}
                    className={iClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-300 mb-1">From Date</label>
                  <input type="date" value={filters.startDate}
                    onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                    className={iClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-300 mb-1">To Date</label>
                  <input type="date" value={filters.endDate}
                    onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                    className={iClass} />
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={handleSearch} disabled={loading}
                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium disabled:opacity-50 transition whitespace-nowrap">
                    {loading ? "Searching..." : "🔍 Search"}
                  </button>
                  <button onClick={clearFilters}
                    className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs font-medium transition whitespace-nowrap">
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Results Table */}
            {records.length > 0 && (
              <div className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs lg:text-sm">
                    <thead className="bg-gray-950 border-b border-gray-700">
                      <tr>
                        {["Created", "Updated", "Think5Whys Code", "Name", "Status", ""].map(h => (
                          <th key={h} className="px-3 py-3 text-left font-medium text-gray-200 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {records.map(rec => (
                        <tr key={rec._id} onClick={() => openDetails(rec)}
                          className="hover:bg-gray-700 cursor-pointer transition">
                          <td className="px-1 py-1 text-gray-100 whitespace-nowrap">{format(new Date(rec.createdAt), "dd MMM yyyy")}</td>
                          <td className="px-1 py-1 text-gray-100 whitespace-nowrap">{format(new Date(rec.updatedAt), "dd MMM yyyy")}</td>
                          <td className="px-1 py-1 font-mono text-indigo-400">{rec.userCode}</td>
                          <td className="px-1 py-1 font-medium text-white">{rec.name || "—"}</td>
                          <td className="px-1 py-1">
                            <span className={`inline-block px-1 py-0.5 rounded-full text-xs font-medium ${
                              rec.isLocked ? "bg-red-900/60 text-red-400" : "bg-emerald-900/60 text-emerald-400"
                            }`}>
                              {rec.isLocked ? "Locked" : rec.status || "Active"}
                            </span>
                          </td>
                          {/* ✅ Fix: per-row loading indicator */}
                          <td className="px-1 py-1 text-indigo-400 font-medium whitespace-nowrap">
                            {loadingRowId === rec._id ? "Loading..." : "View →"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ✅ Fix: true overlay modal with fixed inset-0 */}
          {selectedRecord && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 overflow-y-auto">
              <div className="bg-gray-900 w-full max-w-3xl rounded-xl border border-gray-700 overflow-hidden flex flex-col my-4">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-700 to-violet-700 px-2 py-1 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-white">Participant Details</h2>
                    <p className="text-indigo-200 text-xs mt-0.5">{selectedRecord.name} • {selectedRecord.userCode}</p>
                  </div>
                  <button onClick={closeDetails}
                    className="text-white hover:bg-white/20 w-8 h-8 flex items-center justify-center rounded-full transition text-lg">
                    ✕
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 p-2 lg:p-4 space-y-2 text-xs lg:text-sm overflow-y-auto max-h-[70vh]">
                  <Section title="Participant Information">
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Name"            value={selectedRecord.name} />
                      <Field label="Think5Whys Code" value={selectedRecord.userCode} mono />
                      <Field label="Age"             value={selectedRecord.age} />
                      <Field label="Date"            value={selectedRecord.date} />
                    </div>
                  </Section>

                  <Section title="Module 1 — Deep Thinking">
                    <Field label="Reaction"  value={selectedRecord.m1_reaction} />
                    <Field label="Challenge" value={selectedRecord.m1_challenge} />
                    <Field label="Why 1"     value={selectedRecord.m1_w1} />
                    <Field label="Why 2"     value={selectedRecord.m1_w2} />
                    <Field label="Why 3"     value={selectedRecord.m1_w3} />
                    <Field label="Why 4"     value={selectedRecord.m1_w4} />
                    <Field label="Why 5"     value={selectedRecord.m1_w5} />
                    <Field label="Score"     value={selectedRecord.m1_score && `${selectedRecord.m1_score}/10`} />
                    <Field label="Insight"   value={selectedRecord.m1_insight} />
                  </Section>

                  <Section title="Module 2 — IKIGAI">
                    <Field label="Love"    value={selectedRecord.m2_love} />
                    <Field label="Good at" value={selectedRecord.m2_good} />
                    <Field label="World"   value={selectedRecord.m2_world} />
                    <Field label="Value"   value={selectedRecord.m2_value} />
                    <Field label="IKIGAI"  value={selectedRecord.m2_ikigai} />
                  </Section>

                  <Section title="Module 3 — Attention Signals">
                    <Field label="Biggest Leak" value={selectedRecord.m3_leak} />
                    <Field label="Habit"        value={selectedRecord.m3_habit} />
                  </Section>

                  <Section title="Module 4 — ROACHES">
                    <Field label="Lowest Area" value={selectedRecord.m4_lowest} />
                    <Field label="Action"      value={selectedRecord.m4_action} />
                  </Section>

                  <Section title="Module 5 — Digital Readiness">
                    <Field label="Strength" value={selectedRecord.m5_strength} />
                    <Field label="Gap"      value={selectedRecord.m5_gap} />
                  </Section>

                  <Section title="Module 6 — 5W1H">
                    <Field label="What"  value={selectedRecord.m6_what} />
                    <Field label="Why"   value={selectedRecord.m6_why} />
                    <Field label="Who"   value={selectedRecord.m6_who} />
                    <Field label="When"  value={selectedRecord.m6_when} />
                    <Field label="Where" value={selectedRecord.m6_where} />
                    <Field label="How"   value={selectedRecord.m6_how} />
                    <Field label="Plan"  value={selectedRecord.m6_plan} />
                  </Section>

                  <Section title="Module 7 — GenAI Boundaries">
                    <Field label="Helps with"      value={(selectedRecord.m7_helps || []).join(", ")} />
                    <Field label="Human must lead" value={selectedRecord.m7_human_lead} />
                  </Section>

                  <Section title="Declaration">
                    <Field label="Commits to" value={(selectedRecord.decl_commits || []).join(", ")} />
                    <Field label="Signature"  value={selectedRecord.decl_signature} />
                    <Field label="Discovery"  value={selectedRecord.final_discovery} />
                    <Field label="24h Action" value={selectedRecord.final_action} />
                    <Field label="Vision"     value={selectedRecord.final_vision} />
                  </Section>

                  {/* Moderation Panel */}
                  <div className="bg-gray-800 border border-amber-700 rounded-xl p-4 space-y-3">
                    <p className="text-amber-400 font-semibold text-sm">⚙️ Moderation</p>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={modForm.isLocked}
                        onChange={e => setModForm(f => ({ ...f, isLocked: e.target.checked }))}
                        className="accent-red-500 w-4 h-4" />
                      <span className="text-xs text-gray-100">Lock this record (participant can no longer edit)</span>
                    </label>

                    <div>
                      <p className="text-gray-200 text-xs mb-1">Status</p>
                      <select value={modForm.status}
                        onChange={e => setModForm(f => ({ ...f, status: e.target.value }))}
                        className="bg-gray-900 border border-gray-700 text-xs rounded-xl px-2 py-0.5 focus:outline-none focus:border-indigo-500 text-white w-full lg:w-48">
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div>
                      <p className="text-gray-200 text-xs mb-1">Moderator Remarks</p>
                      <textarea
                        style={{ height: "200px" }}
                        value={modForm.moderatorRemarks}
                        onChange={e => setModForm(f => ({ ...f, moderatorRemarks: e.target.value }))}
                        placeholder="Add remarks for the participant..."
                        className="bg-gray-900 border border-gray-700 text-xs rounded-xl px-3 py-1 focus:outline-none focus:border-indigo-500 text-white w-full resize-none" />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-700 px-2 py-1 flex justify-end gap-2">
                  <button onClick={closeDetails}
                    className="px-2 py-1 border border-gray-600 hover:bg-gray-800 rounded-xl text-xs lg:text-sm font-medium transition">
                    Cancel
                  </button>
                  <button onClick={handleModerate} disabled={loading}
                    className="px-2 py-1 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs lg:text-sm font-medium disabled:opacity-50 transition">
                    {loading ? "Saving..." : "✅ Update Record"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <p className="uppercase tracking-widest text-gray-200 text-[10px] mb-3 font-semibold">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const Field = ({ label, value, mono }) => {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-gray-200 text-[10px] mb-0.5">{label}</p>
      <p className={`bg-gray-800 border border-gray-700 rounded-lg px-3 py-1 text-gray-50 leading-relaxed ${mono ? "font-mono text-indigo-400" : ""}`}>
        {value}
      </p>
    </div>
  );
};

export default PDModerator;